'use client'

import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { identity } from 'es-toolkit'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { fetchJson, fetchJsonWithInit } from '@/helpers/api'

import type {
  GsaCalcRateListResponse,
  GsaContractTaskOrderSummaryBulkResponse,
  GsaContractTaskOrderSummaryLookup,
  GsaELibraryContractListResponse,
  GsaMasSinListResponse,
  GsaScheduleMarketShareResponse,
} from '@/types/data/federal/Gsa'
import type {
  VehicleAwardeeItem,
  VehicleAwardeeListResponse,
  VehicleDetailPageResponse,
  VehicleSearchResponse,
  VehicleTimeSeriesResponse,
} from '@/types/data/federal/Vehicle'
import type { UUID } from '@/types/Id'
import type {
  CompetitionBreakdown,
  ContractSizeHistogram,
  GeographicDistribution,
  SetAsideBreakdown,
  TimeSeriesPoint,
} from '@/types/usaspending'

/**
 * Analytics data stale time (7 days).
 * Matches ISR revalidation period so dehydrated data is never refetched
 * during the ISR window. Federal spending data updates at most daily.
 */
const ANALYTICS_STALE_TIME = 7 * 24 * 60 * 60 * 1000
type VehicleAnalyticsScope = 'default' | 'gsa_schedule'

export const federalVehicleKeys = {
  all: ['federal-vehicles'] as const,
  search: (params: {
    search?: string
    page: number
    limit: number
    ordering: string
  }) => [...federalVehicleKeys.all, 'search', params] as const,
  infiniteSearch: (params: {
    search?: string
    ordering: string
    agency?: string
    setAside?: string
    naicsCode?: string
    pscCode?: string
  }) => [...federalVehicleKeys.all, 'infinite-search', params] as const,
  detail: (params: { id: string }) =>
    [...federalVehicleKeys.all, 'detail', params] as const,
  awardees: (params: {
    id: string
    search?: string
    page: number
    limit: number
  }) => [...federalVehicleKeys.all, 'awardees', params] as const,
  allAwardees: (id: string) =>
    [...federalVehicleKeys.all, 'all-awardees', id] as const,
  gsaScheduleContracts: (params: {
    id: string
    search?: string
    sin?: string
    uei?: string
    page: number
    limit: number
  }) => [...federalVehicleKeys.all, 'gsa-schedule-contracts', params] as const,
  gsaScheduleContractTaskOrders: (params: {
    id: string
    contracts: GsaContractTaskOrderSummaryLookup[]
  }) =>
    [
      ...federalVehicleKeys.all,
      'gsa-schedule-contract-task-orders',
      params,
    ] as const,
  gsaScheduleSins: (params: { id: string; search?: string; limit: number }) =>
    [...federalVehicleKeys.all, 'gsa-schedule-sins', params] as const,
  gsaScheduleLcats: (params: {
    id: string
    contractId: string
    search?: string
    laborCategory?: string
    sin?: string
    category?: string
    page: number
    limit: number
  }) => [...federalVehicleKeys.all, 'gsa-schedule-lcats', params] as const,
  gsaScheduleMarketShare: (params: { id: string; limit: number }) =>
    [...federalVehicleKeys.all, 'gsa-schedule-market-share', params] as const,
  timeSeries: (uuid: string, piids: string[], scope: VehicleAnalyticsScope) =>
    [...federalVehicleKeys.all, 'time-series', uuid, { piids, scope }] as const,
  setAsides: (
    uuid: string,
    piids: string[] | undefined,
    scope: VehicleAnalyticsScope,
  ) =>
    [
      ...federalVehicleKeys.all,
      'set-asides',
      uuid,
      { piids: piids ?? [], scope },
    ] as const,
  competition: (
    uuid: string,
    piids: string[] | undefined,
    scope: VehicleAnalyticsScope,
  ) =>
    [
      ...federalVehicleKeys.all,
      'competition',
      uuid,
      { piids: piids ?? [], scope },
    ] as const,
  geography: (
    uuid: string,
    piids: string[] | undefined,
    scope: VehicleAnalyticsScope,
  ) =>
    [
      ...federalVehicleKeys.all,
      'geography',
      uuid,
      { piids: piids ?? [], scope },
    ] as const,
  contractSizes: (
    uuid: string,
    piids: string[] | undefined,
    scope: VehicleAnalyticsScope,
  ) =>
    [
      ...federalVehicleKeys.all,
      'contract-sizes',
      uuid,
      { piids: piids ?? [], scope },
    ] as const,
}

export async function fetchVehicleSearch(params: {
  accessToken?: string | null
  search?: string
  agency?: string
  setAside?: string
  naicsCode?: string
  pscCode?: string
  page?: number
  limit?: number
  ordering?: string
}): Promise<VehicleSearchResponse> {
  const searchParams = new URLSearchParams()
  if (params.search?.trim()) {
    searchParams.set('search', params.search.trim())
  }
  if (params.agency?.trim()) searchParams.set('agency', params.agency.trim())
  if (params.setAside?.trim()) {
    searchParams.set('set_aside', params.setAside.trim())
  }
  if (params.naicsCode?.trim()) {
    searchParams.set('naics_code', params.naicsCode.trim())
  }
  if (params.pscCode?.trim()) {
    searchParams.set('psc_code', params.pscCode.trim())
  }
  searchParams.set('page', String(params.page ?? 1))
  searchParams.set('limit', String(params.limit ?? 20))
  searchParams.set('ordering', params.ordering ?? '-vehicle_obligations')

  return fetchJson<VehicleSearchResponse>(
    `${API_URL}/api/v1/vehicles/federal/?${searchParams.toString()}`,
    params.accessToken,
  )
}

export async function fetchVehicleDetail(params: {
  accessToken?: string | null
  id: string
}): Promise<VehicleDetailPageResponse> {
  return fetchJson<VehicleDetailPageResponse>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(params.id)}`,
    params.accessToken,
  )
}

export function useVehicleSearch(params: {
  search?: string
  page?: number
  limit?: number
  ordering?: string
}) {
  const { accessToken } = useAuthInfo()
  const normalized = {
    search: params.search?.trim() || undefined,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
    ordering: params.ordering ?? '-vehicle_obligations',
  }

  return useQuery({
    queryKey: federalVehicleKeys.search(normalized),
    queryFn: () => fetchVehicleSearch({ ...normalized, accessToken }),
    enabled: Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: 60 * 1000,
  })
}

export function useVehicleDetail<TData = VehicleDetailPageResponse>(params: {
  id?: string
  enabled?: boolean
  initialData?: VehicleDetailPageResponse
  select?: (data: VehicleDetailPageResponse) => TData
}) {
  const { accessToken } = useAuthInfo()
  const id = params.id ?? ''

  return useQuery({
    queryKey: federalVehicleKeys.detail({ id }),
    queryFn: () => fetchVehicleDetail({ id, accessToken }),
    enabled: Boolean(id) && (params.enabled ?? true),
    placeholderData: (previousData) => previousData,
    initialData: params.initialData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    select: params.select,
  })
}

export async function fetchVehicleAwardees(params: {
  accessToken?: string | null
  id: string
  search?: string
  page?: number
  limit?: number
}): Promise<VehicleAwardeeListResponse> {
  const searchParams = new URLSearchParams()
  if (params.search?.trim()) searchParams.set('search', params.search.trim())
  searchParams.set('page', String(params.page ?? 1))
  searchParams.set('limit', String(params.limit ?? 20))
  return fetchJson<VehicleAwardeeListResponse>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(params.id)}/awardees?${searchParams.toString()}`,
    params.accessToken,
  )
}

export function useVehicleAwardees(params: {
  id?: string
  search?: string
  page?: number
  limit?: number
}) {
  const { accessToken } = useAuthInfo()
  const normalized = {
    id: params.id ?? '',
    search: params.search?.trim() || undefined,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  }
  return useQuery({
    queryKey: federalVehicleKeys.awardees(normalized),
    queryFn: () => fetchVehicleAwardees({ ...normalized, accessToken }),
    enabled: Boolean(normalized.id && accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export async function fetchAllVehicleAwardees(
  accessToken: string | null | undefined,
  id: string,
): Promise<VehicleAwardeeItem[]> {
  return fetchJson<VehicleAwardeeItem[]>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(id)}/all-awardees`,
    accessToken,
  )
}

/**
 * Fetch all awardees for a vehicle (no pagination).
 * Used for analytics chart PIID selection — avoids the 100-awardee-per-page cap.
 */
export function useAllVehicleAwardees(id?: string) {
  const { accessToken } = useAuthInfo()
  return useQuery({
    queryKey: federalVehicleKeys.allAwardees(id ?? ''),
    queryFn: () => fetchAllVehicleAwardees(accessToken, id!),
    enabled: Boolean(id && accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export async function fetchVehicleGsaScheduleContracts(params: {
  accessToken?: string | null
  id: string
  search?: string
  sin?: string
  uei?: string
  page?: number
  limit?: number
}): Promise<GsaELibraryContractListResponse> {
  const searchParams = new URLSearchParams()
  if (params.search?.trim()) searchParams.set('search', params.search.trim())
  if (params.sin?.trim()) searchParams.set('sin', params.sin.trim())
  if (params.uei?.trim()) searchParams.set('uei', params.uei.trim())
  searchParams.set('page', String(params.page ?? 1))
  searchParams.set('limit', String(params.limit ?? 25))

  return fetchJson<GsaELibraryContractListResponse>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(params.id)}/gsa-schedule/contracts?${searchParams.toString()}`,
    params.accessToken,
  )
}

export function useVehicleGsaScheduleContracts(params: {
  id?: string
  search?: string
  sin?: string
  uei?: string
  page?: number
  limit?: number
}) {
  const { accessToken } = useAuthInfo()
  const normalized = {
    id: params.id ?? '',
    search: params.search?.trim() || undefined,
    sin: params.sin?.trim() || undefined,
    uei: params.uei?.trim() || undefined,
    page: params.page ?? 1,
    limit: params.limit ?? 25,
  }

  return useQuery({
    queryKey: federalVehicleKeys.gsaScheduleContracts(normalized),
    queryFn: () =>
      fetchVehicleGsaScheduleContracts({ ...normalized, accessToken }),
    enabled: Boolean(normalized.id && accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export async function fetchVehicleGsaScheduleContractTaskOrders(params: {
  accessToken?: string | null
  id: string
  contracts: GsaContractTaskOrderSummaryLookup[]
}): Promise<GsaContractTaskOrderSummaryBulkResponse> {
  return fetchJsonWithInit<GsaContractTaskOrderSummaryBulkResponse>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(params.id)}/gsa-schedule/contracts/task-orders`,
    params.accessToken,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contracts: params.contracts,
      }),
    },
  )
}

export function useVehicleGsaScheduleContractTaskOrders(params: {
  id?: string
  contracts: GsaContractTaskOrderSummaryLookup[]
  enabled?: boolean
}) {
  const { accessToken } = useAuthInfo()
  const normalized = {
    id: params.id ?? '',
    contracts: params.contracts
      .filter((contract) => contract.contract_uuid)
      .reduce<GsaContractTaskOrderSummaryLookup[]>((acc, contract) => {
        if (acc.some((item) => item.contract_uuid === contract.contract_uuid)) {
          return acc
        }
        acc.push({
          contract_uuid: contract.contract_uuid,
          idv_key: contract.idv_key,
        })
        return acc
      }, []),
  }

  return useQuery({
    queryKey: federalVehicleKeys.gsaScheduleContractTaskOrders(normalized),
    queryFn: () =>
      fetchVehicleGsaScheduleContractTaskOrders({
        ...normalized,
        accessToken,
      }),
    enabled:
      Boolean(normalized.id && accessToken) &&
      normalized.contracts.length > 0 &&
      (params.enabled ?? true),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export async function fetchVehicleGsaScheduleMasSins(params: {
  accessToken?: string | null
  id: string
  search?: string
  limit?: number
}): Promise<GsaMasSinListResponse> {
  const searchParams = new URLSearchParams()
  if (params.search?.trim()) searchParams.set('search', params.search.trim())
  searchParams.set('limit', String(params.limit ?? 8))

  return fetchJson<GsaMasSinListResponse>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(params.id)}/gsa-schedule/sins?${searchParams.toString()}`,
    params.accessToken,
  )
}

export function useVehicleGsaScheduleMasSins(params: {
  id?: string
  search?: string
  limit?: number
  enabled?: boolean
}) {
  const { accessToken } = useAuthInfo()
  const normalized = {
    id: params.id ?? '',
    search: params.search?.trim() || undefined,
    limit: params.limit ?? 8,
  }

  return useQuery({
    queryKey: federalVehicleKeys.gsaScheduleSins(normalized),
    queryFn: () =>
      fetchVehicleGsaScheduleMasSins({ ...normalized, accessToken }),
    enabled:
      Boolean(normalized.id && accessToken) &&
      (params.enabled ?? Boolean(normalized.search)),
    placeholderData: (previousData) => previousData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export async function fetchVehicleGsaScheduleLcats(params: {
  accessToken?: string | null
  id: string
  contractId: string
  search?: string
  laborCategory?: string
  sin?: string
  category?: string
  educationLevel?: string
  securityClearance?: string
  worksite?: string
  businessSize?: string
  priceGte?: string
  priceLte?: string
  minExperienceGte?: string
  minExperienceLte?: string
  ordering?: string
  page?: number
  limit?: number
}): Promise<GsaCalcRateListResponse> {
  const searchParams = new URLSearchParams()
  if (params.search?.trim()) searchParams.set('search', params.search.trim())
  if (params.laborCategory?.trim()) {
    searchParams.set('labor_category', params.laborCategory.trim())
  }
  if (params.sin?.trim()) searchParams.set('sin', params.sin.trim())
  if (params.category?.trim()) {
    searchParams.set('category', params.category.trim())
  }
  if (params.educationLevel)
    searchParams.set('education_level', params.educationLevel)
  if (params.securityClearance)
    searchParams.set('security_clearance', params.securityClearance)
  if (params.worksite) searchParams.set('worksite', params.worksite)
  if (params.businessSize)
    searchParams.set('business_size', params.businessSize)
  if (params.priceGte) searchParams.set('price_gte', params.priceGte)
  if (params.priceLte) searchParams.set('price_lte', params.priceLte)
  if (params.minExperienceGte)
    searchParams.set('min_years_experience_gte', params.minExperienceGte)
  if (params.minExperienceLte)
    searchParams.set('min_years_experience_lte', params.minExperienceLte)
  if (params.ordering) searchParams.set('ordering', params.ordering)
  searchParams.set('page', String(params.page ?? 1))
  searchParams.set('limit', String(params.limit ?? 25))

  return fetchJson<GsaCalcRateListResponse>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(params.id)}/gsa-schedule/contracts/${encodeURIComponent(params.contractId)}/lcats?${searchParams.toString()}`,
    params.accessToken,
  )
}

export function useVehicleGsaScheduleLcats(params: {
  id?: string
  contractId?: string
  search?: string
  laborCategory?: string
  sin?: string
  category?: string
  educationLevel?: string
  securityClearance?: string
  worksite?: string
  businessSize?: string
  priceGte?: string
  priceLte?: string
  minExperienceGte?: string
  minExperienceLte?: string
  ordering?: string
  page?: number
  limit?: number
}) {
  const { accessToken } = useAuthInfo()
  const normalized = {
    id: params.id ?? '',
    contractId: params.contractId ?? '',
    search: params.search?.trim() || undefined,
    laborCategory: params.laborCategory?.trim() || undefined,
    sin: params.sin?.trim() || undefined,
    category: params.category?.trim() || undefined,
    educationLevel: params.educationLevel || undefined,
    securityClearance: params.securityClearance || undefined,
    worksite: params.worksite || undefined,
    businessSize: params.businessSize || undefined,
    priceGte: params.priceGte || undefined,
    priceLte: params.priceLte || undefined,
    minExperienceGte: params.minExperienceGte || undefined,
    minExperienceLte: params.minExperienceLte || undefined,
    ordering: params.ordering || undefined,
    page: params.page ?? 1,
    limit: params.limit ?? 25,
  }

  return useQuery({
    queryKey: federalVehicleKeys.gsaScheduleLcats(normalized),
    queryFn: () => fetchVehicleGsaScheduleLcats({ ...normalized, accessToken }),
    enabled: Boolean(normalized.id && normalized.contractId && accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export async function fetchVehicleGsaScheduleMarketShare(params: {
  accessToken?: string | null
  id: string
  limit?: number
}): Promise<GsaScheduleMarketShareResponse> {
  const searchParams = new URLSearchParams()
  searchParams.set('limit', String(params.limit ?? 10))
  return fetchJson<GsaScheduleMarketShareResponse>(
    `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(params.id)}/gsa-schedule/analytics/market-share?${searchParams.toString()}`,
    params.accessToken,
  )
}

export function useVehicleGsaScheduleMarketShare(params: {
  id?: string
  limit?: number
  enabled?: boolean
}) {
  const { accessToken } = useAuthInfo()
  const normalized = {
    id: params.id ?? '',
    limit: params.limit ?? 10,
  }

  return useQuery({
    queryKey: federalVehicleKeys.gsaScheduleMarketShare(normalized),
    queryFn: () =>
      fetchVehicleGsaScheduleMarketShare({ ...normalized, accessToken }),
    enabled: Boolean(normalized.id && accessToken && (params.enabled ?? true)),
    staleTime: ANALYTICS_STALE_TIME,
    refetchOnWindowFocus: false,
  })
}

interface RawTimeSeriesPoint {
  year: number
  month: number
  amount: string // Pydantic Decimal serializes as JSON string
  transaction_count: number // snake_case from backend
  date?: string
}

interface RawVehicleTimeSeriesResponse {
  combined: RawTimeSeriesPoint[]
  children: Array<{ piid: string; points: RawTimeSeriesPoint[] }>
}

function transformPoint(p: RawTimeSeriesPoint): TimeSeriesPoint {
  return {
    year: p.year,
    month: p.month,
    amount: Number(p.amount),
    transactionCount: p.transaction_count,
    date: p.date,
  }
}

export async function fetchVehicleTimeSeries(
  accessToken: string | null | undefined,
  uuid: string,
  piids: string[],
  scope: VehicleAnalyticsScope = 'default',
): Promise<VehicleTimeSeriesResponse> {
  const raw =
    scope === 'default'
      ? await fetchJsonWithInit<RawVehicleTimeSeriesResponse>(
          buildAnalyticsUrl(uuid, 'time-series', undefined, scope),
          accessToken,
          buildVehicleAnalyticsInit({ piids }),
        )
      : await fetchJson<RawVehicleTimeSeriesResponse>(
          buildAnalyticsUrl(uuid, 'time-series', piids, scope),
          accessToken,
        )
  return {
    combined: raw.combined.map(transformPoint),
    children: raw.children.map((c) => ({
      piid: c.piid,
      points: c.points.map(transformPoint),
    })),
  }
}

interface UseVehicleTimeSeriesParams {
  uuid: UUID
  piids: string[]
  scope?: VehicleAnalyticsScope
}

/**
 * Fetch per-awardee obligation time series for a vehicle.
 * Enabled only when piids is non-empty.
 */
export function useVehicleTimeSeries(params: UseVehicleTimeSeriesParams) {
  const { uuid, piids, scope = 'default' } = params
  const { accessToken } = useAuthInfo()
  return useQuery({
    queryKey: federalVehicleKeys.timeSeries(uuid, piids, scope),
    queryFn: () => fetchVehicleTimeSeries(accessToken, uuid, piids, scope),
    enabled:
      scope === 'gsa_schedule'
        ? Boolean(accessToken)
        : piids.length > 0 && Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: ANALYTICS_STALE_TIME,
    refetchOnWindowFocus: false,
  })
}

// ---------------------------------------------------------------------------
// Vehicle analytics: set-asides, competition, geography, contract sizes
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Raw API response types (snake_case from backend)
// ---------------------------------------------------------------------------

interface ApiAnalyticsEnvelope<T> {
  entity_id: string
  fiscal_year?: number | null
  results: T[]
}

interface VehicleAnalyticsPiidsRequest {
  piids: string[]
}

interface VehicleAnalyticsGeographyRequest extends VehicleAnalyticsPiidsRequest {
  limit?: number
}

interface ApiSetAside {
  type_code: string | null
  type_name: string
  amount: string | number
  percentage: string | number
  award_count: number
}

interface ApiCompetition {
  extent_competed: string | null
  description: string
  amount: string | number
  percentage: string | number
  award_count: number
}

interface ApiGeography {
  state_code: string | null
  state_name: string | null
  amount: string | number
  percentage: string | number
  award_count: number
}

interface ApiContractSize {
  bucket: string
  min_value: number
  max_value: number
  count: number
  total_amount: string | number
}

function buildAnalyticsUrl(
  uuid: string,
  endpoint: string,
  piids?: string[],
  scope: VehicleAnalyticsScope = 'default',
): string {
  const searchParams = new URLSearchParams()
  for (const p of piids ?? []) searchParams.append('piids', p)
  const query = searchParams.toString()
  const base =
    scope === 'gsa_schedule'
      ? `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(uuid)}/gsa-schedule/analytics/${endpoint}`
      : `${API_URL}/api/v1/vehicles/federal/${encodeURIComponent(uuid)}/analytics/${endpoint}`
  return query ? `${base}?${query}` : base
}

function buildVehicleAnalyticsInit(
  body: VehicleAnalyticsPiidsRequest | VehicleAnalyticsGeographyRequest,
): RequestInit {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }
}

interface UseVehicleSetAsidesParams {
  uuid: UUID
  piids?: string[]
  scope?: VehicleAnalyticsScope
}

export function useVehicleSetAsides(params: UseVehicleSetAsidesParams) {
  const { uuid, piids, scope = 'default' } = params
  const { accessToken } = useAuthInfo()
  const shouldUseExplicitSelection = scope === 'default' && piids !== undefined
  return useQuery({
    queryKey: federalVehicleKeys.setAsides(uuid, piids, scope),
    queryFn: () =>
      (shouldUseExplicitSelection
        ? fetchJsonWithInit<ApiAnalyticsEnvelope<ApiSetAside>>(
            buildAnalyticsUrl(uuid, 'set-asides', undefined, scope),
            accessToken,
            buildVehicleAnalyticsInit({ piids: piids ?? [] }),
          )
        : fetchJson<ApiAnalyticsEnvelope<ApiSetAside>>(
            buildAnalyticsUrl(uuid, 'set-asides', piids, scope),
            accessToken,
          )
      ).then((r) =>
        r.results.map(
          (d): SetAsideBreakdown => ({
            typeCode: d.type_code,
            typeName: d.type_name,
            amount: Number(d.amount),
            percentage: Number(d.percentage),
            awardCount: d.award_count,
          }),
        ),
      ),
    enabled: shouldUseExplicitSelection
      ? Boolean(accessToken) && (piids?.length ?? 0) > 0
      : Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: ANALYTICS_STALE_TIME,
    refetchOnWindowFocus: false,
  })
}

interface UseVehicleCompetitionParams {
  uuid: UUID
  piids?: string[]
  scope?: VehicleAnalyticsScope
}

export function useVehicleCompetition(params: UseVehicleCompetitionParams) {
  const { uuid, piids, scope = 'default' } = params
  const { accessToken } = useAuthInfo()
  const shouldUseExplicitSelection = scope === 'default' && piids !== undefined
  return useQuery({
    queryKey: federalVehicleKeys.competition(uuid, piids, scope),
    queryFn: () =>
      (shouldUseExplicitSelection
        ? fetchJsonWithInit<ApiAnalyticsEnvelope<ApiCompetition>>(
            buildAnalyticsUrl(uuid, 'competition', undefined, scope),
            accessToken,
            buildVehicleAnalyticsInit({ piids: piids ?? [] }),
          )
        : fetchJson<ApiAnalyticsEnvelope<ApiCompetition>>(
            buildAnalyticsUrl(uuid, 'competition', piids, scope),
            accessToken,
          )
      ).then((r) =>
        r.results.map(
          (d): CompetitionBreakdown => ({
            extentCompeted: d.extent_competed,
            description: d.description,
            amount: Number(d.amount),
            percentage: Number(d.percentage),
            awardCount: d.award_count,
          }),
        ),
      ),
    enabled: shouldUseExplicitSelection
      ? Boolean(accessToken) && (piids?.length ?? 0) > 0
      : Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: ANALYTICS_STALE_TIME,
    refetchOnWindowFocus: false,
  })
}

interface UseVehicleGeographyParams {
  uuid: UUID
  piids?: string[]
  scope?: VehicleAnalyticsScope
}

export function useVehicleGeography(params: UseVehicleGeographyParams) {
  const { uuid, piids, scope = 'default' } = params
  const { accessToken } = useAuthInfo()
  const shouldUseExplicitSelection = scope === 'default' && piids !== undefined
  return useQuery({
    queryKey: federalVehicleKeys.geography(uuid, piids, scope),
    queryFn: () =>
      (shouldUseExplicitSelection
        ? fetchJsonWithInit<ApiAnalyticsEnvelope<ApiGeography>>(
            buildAnalyticsUrl(uuid, 'geography', undefined, scope),
            accessToken,
            buildVehicleAnalyticsInit({ piids: piids ?? [] }),
          )
        : fetchJson<ApiAnalyticsEnvelope<ApiGeography>>(
            buildAnalyticsUrl(uuid, 'geography', piids, scope),
            accessToken,
          )
      ).then((r) =>
        r.results.map(
          (d): GeographicDistribution => ({
            stateCode: d.state_code,
            stateName: d.state_name,
            amount: Number(d.amount),
            percentage: Number(d.percentage),
            awardCount: d.award_count,
          }),
        ),
      ),
    enabled: shouldUseExplicitSelection
      ? Boolean(accessToken) && (piids?.length ?? 0) > 0
      : Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: ANALYTICS_STALE_TIME,
    refetchOnWindowFocus: false,
  })
}

interface UseVehicleContractSizesParams {
  uuid: UUID
  piids?: string[]
  scope?: VehicleAnalyticsScope
}

export function useVehicleContractSizes(params: UseVehicleContractSizesParams) {
  const { uuid, piids, scope = 'default' } = params
  const { accessToken } = useAuthInfo()
  const shouldUseExplicitSelection = scope === 'default' && piids !== undefined
  return useQuery({
    queryKey: federalVehicleKeys.contractSizes(uuid, piids, scope),
    queryFn: () =>
      (shouldUseExplicitSelection
        ? fetchJsonWithInit<ApiAnalyticsEnvelope<ApiContractSize>>(
            buildAnalyticsUrl(uuid, 'contract-sizes', undefined, scope),
            accessToken,
            buildVehicleAnalyticsInit({ piids: piids ?? [] }),
          )
        : fetchJson<ApiAnalyticsEnvelope<ApiContractSize>>(
            buildAnalyticsUrl(uuid, 'contract-sizes', piids, scope),
            accessToken,
          )
      ).then((r) =>
        r.results.map(
          (d): ContractSizeHistogram => ({
            bucket: d.bucket,
            minValue: d.min_value,
            maxValue: d.max_value,
            count: d.count,
            totalAmount: Number(d.total_amount),
          }),
        ),
      ),
    enabled: shouldUseExplicitSelection
      ? Boolean(accessToken) && (piids?.length ?? 0) > 0
      : Boolean(accessToken),
    placeholderData: (previousData) => previousData,
    staleTime: ANALYTICS_STALE_TIME,
    refetchOnWindowFocus: false,
  })
}

// ---------------------------------------------------------------------------
// Infinite vehicle search (for virtual scroll / incremental loading)
// ---------------------------------------------------------------------------

function getNextVehiclePage(nextUrl: string | null): number | undefined {
  if (!nextUrl) return undefined
  try {
    const parsed = new URL(nextUrl, API_URL)
    const page = Number.parseInt(parsed.searchParams.get('page') ?? '', 10)
    return Number.isFinite(page) ? page : undefined
  } catch {
    return undefined
  }
}

interface UseInfiniteVehicleSearchParams<T> {
  search?: string
  ordering?: string
  agency?: string
  setAside?: string
  naicsCode?: string
  pscCode?: string
  enabled?: boolean
  select?: (data: InfiniteData<VehicleSearchResponse, number>) => T
}

export function useInfiniteVehicleSearch<
  T = InfiniteData<VehicleSearchResponse, number>,
>(params: UseInfiniteVehicleSearchParams<T>) {
  const { accessToken } = useAuthInfo()
  const { enabled, select, ...rest } = params
  const normalized = {
    search: rest.search?.trim() || undefined,
    ordering: rest.ordering ?? '-vehicle_obligations',
    agency: rest.agency?.trim() || undefined,
    setAside: rest.setAside?.trim() || undefined,
    naicsCode: rest.naicsCode?.trim() || undefined,
    pscCode: rest.pscCode?.trim() || undefined,
  }

  return useInfiniteQuery({
    queryKey: federalVehicleKeys.infiniteSearch(normalized),
    queryFn: ({ pageParam }) =>
      fetchVehicleSearch({ ...normalized, page: pageParam, accessToken }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => getNextVehiclePage(lastPage.next),
    enabled: (enabled ?? true) && Boolean(accessToken),
    placeholderData: identity,
    select,
    staleTime: 60 * 1000,
  })
}
